import {
  dismissHealthIssues,
  fetchHealthData,
  fetchHealthDismissals,
  restoreHealthIssues,
} from "@a/health";
import { useAuth } from "@h/useCtx";
import { useBusinessHealth } from "@h/useBusinessHealth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { aggregateHealth, type HealthIssue } from "@u/contentHealth";
import { useCallback, useMemo } from "react";

const DISMISS_KEY = ["content-health-dismissals"] as const;

/**
 * Live content-completeness ("avisos") for the signed-in owner. Aggregates
 * untranslated names/hotspots y tours vacíos/sin publicar/sin salida en una
 * lista plana y ordenada. Recomputado en cada refetch — el aviso desaparece
 * the moment the owner fills the gap. Deliberate avisos can be dismissed (hidden
 * until restored) so the badge can reach zero.
 */
export function useContentHealth() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["content-health", user?.id],
    queryFn: () => fetchHealthData(user!.id),
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });

  const dismissals = useQuery({
    queryKey: DISMISS_KEY,
    queryFn: fetchHealthDismissals,
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });

  const business = useBusinessHealth();

  const all: HealthIssue[] = useMemo(
    () =>
      query.data
        ? aggregateHealth(query.data.tours, business)
        : business,
    [query.data, business],
  );

  const dismissedSet = useMemo(
    () => new Set(dismissals.data ?? []),
    [dismissals.data],
  );

  // Business avisos are time-derived (they reappear as soon as the condition
  // holds again), so hiding one forever makes no sense — they ignore dismissals
  // and their rows don't offer the control.
  const issues = useMemo(
    () =>
      all.filter((i) => i.product === "business" || !dismissedSet.has(i.id)),
    [all, dismissedSet],
  );
  const dismissedIssues = useMemo(
    () => all.filter((i) => i.product !== "business" && dismissedSet.has(i.id)),
    [all, dismissedSet],
  );

  // Count of visible avisos per resource — cards look up their tour/venue.
  const byResource: Record<string, number> = useMemo(() => {
    const map: Record<string, number> = {};
    for (const i of issues) map[i.resourceId] = (map[i.resourceId] ?? 0) + 1;
    return map;
  }, [issues]);

  const dismissMut = useMutation({
    mutationFn: dismissHealthIssues,
    onSuccess: () => qc.invalidateQueries({ queryKey: DISMISS_KEY }),
  });
  const restoreMut = useMutation({
    mutationFn: restoreHealthIssues,
    onSuccess: () => qc.invalidateQueries({ queryKey: DISMISS_KEY }),
  });

  // Stable identities — HealthRow is memo'd and these are passed down to every
  // row; fresh arrows on each render would defeat it.
  const dismissMutate = dismissMut.mutate;
  const restoreMutate = restoreMut.mutate;
  const dismiss = useCallback(
    (keys: string | string[]) =>
      dismissMutate(Array.isArray(keys) ? keys : [keys]),
    [dismissMutate],
  );
  const restore = useCallback(
    (keys: string | string[]) =>
      restoreMutate(Array.isArray(keys) ? keys : [keys]),
    [restoreMutate],
  );

  return {
    issues,
    dismissedIssues,
    count: issues.length,
    byResource,
    isLoading: query.isLoading,
    // Surfaced so the bell/panel can distinguish "all healthy" from "check
    // failed" — otherwise a broken fetch reads as a false green (count 0).
    isError: query.isError,
    dismiss,
    restore,
  };
}

